import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { PREF_KEYS, PREF_DEFAULTS } from '@maker-toolchest/shared';
import { Button } from '@/components/Button';
import { useToast } from '@/components/ToastProvider';
import {
  useUserPreference,
  useSetUserPreferences,
} from '@/features/preferences';
import { preferencesCache } from '@/lib/preferencesCache';
import { useAuth } from '@/hooks/useAuth';

export function NotificationsPage() {
  const { user } = useAuth();
  const toast = useToast();

  // Load preferences from API
  const { data: emailLowFilament, isLoading: loadingLowFilament } = useUserPreference<boolean>(
    PREF_KEYS.NOTIFICATIONS.EMAIL_LOW_FILAMENT,
    PREF_DEFAULTS.NOTIFICATIONS.EMAIL_LOW_FILAMENT
  );
  const { data: emailJobComplete, isLoading: loadingJobComplete } = useUserPreference<boolean>(
    PREF_KEYS.NOTIFICATIONS.EMAIL_JOB_COMPLETE,
    PREF_DEFAULTS.NOTIFICATIONS.EMAIL_JOB_COMPLETE
  );
  const { data: emailJobFailed, isLoading: loadingJobFailed } = useUserPreference<boolean>(
    PREF_KEYS.NOTIFICATIONS.EMAIL_JOB_FAILED,
    PREF_DEFAULTS.NOTIFICATIONS.EMAIL_JOB_FAILED
  );
  const { data: inAppEnabled, isLoading: loadingInApp } = useUserPreference<boolean>(
    PREF_KEYS.NOTIFICATIONS.IN_APP_ENABLED,
    PREF_DEFAULTS.NOTIFICATIONS.IN_APP_ENABLED
  );

  // Local form state
  const [emailLowFilamentLocal, setEmailLowFilamentLocal] = useState(emailLowFilament ?? true);
  const [emailJobCompleteLocal, setEmailJobCompleteLocal] = useState(emailJobComplete ?? true);
  const [emailJobFailedLocal, setEmailJobFailedLocal] = useState(emailJobFailed ?? true);
  const [inAppEnabledLocal, setInAppEnabledLocal] = useState(inAppEnabled ?? true);

  // Update local state when preferences load
  useEffect(() => {
    if (emailLowFilament !== undefined) setEmailLowFilamentLocal(emailLowFilament);
  }, [emailLowFilament]);
  useEffect(() => {
    if (emailJobComplete !== undefined) setEmailJobCompleteLocal(emailJobComplete);
  }, [emailJobComplete]);
  useEffect(() => {
    if (emailJobFailed !== undefined) setEmailJobFailedLocal(emailJobFailed);
  }, [emailJobFailed]);
  useEffect(() => {
    if (inAppEnabled !== undefined) setInAppEnabledLocal(inAppEnabled);
  }, [inAppEnabled]);

  const setUserPreferences = useSetUserPreferences();

  const isLoading = loadingLowFilament || loadingJobComplete || loadingJobFailed || loadingInApp;
  const hasChanges =
    emailLowFilamentLocal !== emailLowFilament ||
    emailJobCompleteLocal !== emailJobComplete ||
    emailJobFailedLocal !== emailJobFailed ||
    inAppEnabledLocal !== inAppEnabled;

  const handleSave = async () => {
    try {
      await setUserPreferences.mutateAsync([
        { key: PREF_KEYS.NOTIFICATIONS.EMAIL_LOW_FILAMENT, value: emailLowFilamentLocal },
        { key: PREF_KEYS.NOTIFICATIONS.EMAIL_JOB_COMPLETE, value: emailJobCompleteLocal },
        { key: PREF_KEYS.NOTIFICATIONS.EMAIL_JOB_FAILED, value: emailJobFailedLocal },
        { key: PREF_KEYS.NOTIFICATIONS.IN_APP_ENABLED, value: inAppEnabledLocal },
      ]);

      // Update cache on success
      if (user?.id) {
        preferencesCache.setMany(user.id, {
          [PREF_KEYS.NOTIFICATIONS.EMAIL_LOW_FILAMENT]: emailLowFilamentLocal,
          [PREF_KEYS.NOTIFICATIONS.EMAIL_JOB_COMPLETE]: emailJobCompleteLocal,
          [PREF_KEYS.NOTIFICATIONS.EMAIL_JOB_FAILED]: emailJobFailedLocal,
          [PREF_KEYS.NOTIFICATIONS.IN_APP_ENABLED]: inAppEnabledLocal,
        });
      }

      toast.success('Notification preferences saved successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save notification preferences');
    }
  };

  const handleCancel = () => {
    setEmailLowFilamentLocal(emailLowFilament ?? true);
    setEmailJobCompleteLocal(emailJobComplete ?? true);
    setEmailJobFailedLocal(emailJobFailed ?? true);
    setInAppEnabledLocal(inAppEnabled ?? true);
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-foreground-muted" />
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Page header */}
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-foreground">Notifications</h1>
        <p className="text-sm text-foreground-muted">Configure how you receive alerts and updates</p>
      </div>

      {/* Form */}
      <div className="max-w-2xl space-y-6">
        {/* Email Notifications */}
        <div className="rounded border border-border bg-background-subtle p-4">
          <h3 className="text-sm font-medium text-foreground mb-3">Email Notifications</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={emailLowFilamentLocal}
                onChange={(e) => setEmailLowFilamentLocal(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground">Low filament alerts</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={emailJobCompleteLocal}
                onChange={(e) => setEmailJobCompleteLocal(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground">Job completion notifications</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={emailJobFailedLocal}
                onChange={(e) => setEmailJobFailedLocal(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground">Job failure notifications</span>
            </label>
          </div>
        </div>

        {/* In-App Notifications */}
        <div className="rounded border border-border bg-background-subtle p-4">
          <h3 className="text-sm font-medium text-foreground mb-3">In-App Notifications</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={inAppEnabledLocal}
                onChange={(e) => setInAppEnabledLocal(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground">Enable in-app notifications</span>
            </label>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={!hasChanges || setUserPreferences.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges || setUserPreferences.isPending}
          >
            {setUserPreferences.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
